import $ from 'cash-dom';
import {
    getCameraTarget,
    getDataProp,
    getObjectByName,
    getQueryParam,
} from '../utils';
import { Logger } from '../utils/logger';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { PerspectiveCamera } from 'three';
import { loadScene } from '../loader';
import {
    QUERY_PARAM__SCENE,
    QUERY_PARAM__BACKGROUND,
    QUERY_PARAM__ENVIRONMENT_MAP,
    QUERY_PARAM__USE_ENV_MAP_AS_BACKGROUND,
    NAME__CAMERA_NAME,
} from '../constants';
import { createRenderer } from '../renderers';
import { createEnvironmentMap } from '../renderers/factories/environment-map.factory';
import { createBackground } from '../renderers/factories/background.factory';

const FOV__MIN = 10;
const FOV__MAX = 90;

export { createModelViewer };

async function createModelViewer(
    selectorContainer,
    {
        autoRotate,
        basePathBackground,
        basePathEnvironment,
        backgroundFileName,
        environmentFileName,
    } = {}
) {
    const pathToScene = _getPathToScene(selectorContainer);
    const useEnvMapAsBackground =
        getQueryParam(QUERY_PARAM__USE_ENV_MAP_AS_BACKGROUND) === 'true';
    const canvas = _getCanvasProperties(selectorContainer);
    const renderer = createRenderer(canvas);

    // Use environment-path from query-parameter (if set)
    {
        const [basePath, fileName] = _getPathToEnvironmentMap();
        basePathEnvironment = basePath || basePathEnvironment;
        environmentFileName = fileName || environmentFileName;
    }
    // Use background-path from query-parameter (if set)
    {
        const [basePath, fileName] = _getPathToBackground(
            useEnvMapAsBackground
        );
        basePathBackground = basePath || basePathBackground;
        backgroundFileName = fileName || backgroundFileName;
    }

    const [loadedScene, background, environmentMap] = await Promise.all([
        loadScene(pathToScene),
        createBackground(
            basePathBackground,
            backgroundFileName,
            useEnvMapAsBackground,
            {
                environmentFileName,
                basePathEnvironment,
            }
        ),
        createEnvironmentMap(renderer, environmentFileName, {
            basePath: basePathEnvironment,
        }),
    ]);
    const camera = _createCamera(loadedScene, canvas);
    const controls = _createControls(selectorContainer, camera, renderer, {
        autoRotate,
    });

    _aimCamera(loadedScene, controls);

    return {
        selectorContainer,
        pathToScene,
        renderer,
        scene: _createScene(),
        loadedScene,
        background,
        environmentMap,
        camera,
        controls,
        initialValues: {
            fov: camera.fov,
        },
        props: {
            canvas,
            basePathBackground,
            basePathEnvironment,
            useEnvMapAsBackground,
        },
    };
}

function _getPathToScene(selectorContainer) {
    return (
        _getExternalScenePath() || getDataProp(selectorContainer, 'srcModel')
    );
}

function _getPathToEnvironmentMap() {
    const url = _getExternalEnvironmentMapPath();
    return _splitBasePathAndFileName(url);
}

function _getPathToBackground(useEnvironmentMapAsBackground) {
    if (useEnvironmentMapAsBackground) {
        return _getPathToEnvironmentMap();
    }
    const url = _getExternalBackgroundPath();
    return _splitBasePathAndFileName(url);
}

function _splitBasePathAndFileName(url) {
    if (!url) return [];
    const segs = url.split('/');
    const fileName = segs.pop();
    const basePath = segs.length ? `${segs.join('/')}/` : '';
    return [basePath, fileName];
}

function _getExternalScenePath() {
    return _getResourceUrl(QUERY_PARAM__SCENE);
}

function _getExternalEnvironmentMapPath() {
    return _getResourceUrl(QUERY_PARAM__ENVIRONMENT_MAP);
}

function _getExternalBackgroundPath() {
    return _getResourceUrl(QUERY_PARAM__BACKGROUND);
}

function _getResourceUrl(paramName) {
    const resourceUrl = getQueryParam(paramName);
    if (!resourceUrl) {
        Logger.debug(`No value for ${paramName} found`);
        return null;
    }
    if (!resourceUrl.match(/^http(s)?:\/\//)) {
        Logger.debug(`"${resourceUrl}" for "${paramName}" found`);
        return resourceUrl;
    }
    // `ALLOWED_EXTERNAL_HOSTNAMES` is injected via webpack
    //noinspection JSUnresolvedVariable
    const allowedHostnames = (ALLOWED_EXTERNAL_HOSTNAMES || '').split(',');
    const hostname = new URL(resourceUrl).hostname;
    if (!allowedHostnames.includes(hostname)) {
        Logger.debug(
            `"${resourceUrl}" is not in the list of allowed hostnames, for "${paramName}"`
        );
        return null;
    }
    return resourceUrl;
}

function _getCanvasProperties(selector) {
    const jqCont = $(selector);
    return {
        width: jqCont.innerWidth(),
        height: jqCont.innerHeight(),
    };
}

function _createScene() {
    return new THREE.Scene();
}

function _createCamera(loadedScene, canvas) {
    const { width, height } = canvas;
    const cameraGroup = getObjectByName(loadedScene, NAME__CAMERA_NAME);
    const camera = _getCameraObject(cameraGroup);
    camera.aspect = width / height;

    _setCameraPosition(
        camera,
        cameraGroup.position.x,
        cameraGroup.position.y,
        cameraGroup.position.z
    );
    camera.updateProjectionMatrix();

    return camera;
}

function _createControls(selectorContainer, camera, renderer, { autoRotate }) {
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 0.5, 0);
    controls.update();
    // Enabling pan won't apply for the background. It's just like moving the object
    controls.enablePan = true;
    controls.enableDamping = true;
    // No dollying of camera for zooming. Instead, change fov (to make the background zoom as well)
    controls.enableZoom = false;
    _addZoomControls(selectorContainer, camera);

    autoRotate && _enableAutoRotate(controls);
    return controls;
}

function _enableAutoRotate(controls) {
    controls.autoRotate = true;
    // Disable auto-rotate on user interaction
    controls.addEventListener(
        'start',
        () => {
            controls.autoRotate = false;
        },
        false
    );
}

function _aimCamera(loadedScene, controls) {
    // The target, where the camera looks at, is determined by the object's (named "camera_target") position
    const cameraTarget = getCameraTarget(loadedScene);
    const targetPosition = cameraTarget.position;

    _setCameraTarget(
        controls,
        targetPosition.x,
        targetPosition.y,
        targetPosition.z
    );
}

function _getCameraObject(cameraGroup) {
    if (cameraGroup instanceof PerspectiveCamera) return cameraGroup;
    return cameraGroup.children.find(
        (object) => object instanceof PerspectiveCamera
    );
}

function _setCameraTarget(controls, x, y, z) {
    controls.target.set(x, y, z);
}

function _setCameraPosition(camera, x, y, z) {
    camera.position.set(x, y, z);
}

function _addZoomControls(selectorContainer, camera) {
    document
        .querySelector(selectorContainer)
        .addEventListener('wheel', (evt) => _zoom(camera, evt));
}

function _zoom(camera, event) {
    const fov = camera.fov + event.deltaY * 0.05;
    camera.fov = THREE.MathUtils.clamp(fov, FOV__MIN, FOV__MAX);
    camera.updateProjectionMatrix();
}
